// packages/contracts/src/notification-delivery.ts

/**
 * Whether anything this gateway queues as a notification can actually leave
 * the building as an email.
 *
 * ⚠ A CAPABILITY, not a deployment name — the fourth application of the
 * distinction {@link IdentityProvider}, {@link SettlementDispatch} and
 * {@link DemonstrationMode} already carry, and it is here for a defect that had
 * already happened rather than for symmetry.
 *
 * ## What this is here to answer
 *
 * `services/gateway/src/routes/notifications.ts` returned a fixed sentence on
 * two live API responses: *"No email delivery mechanism exists anywhere in this
 * system. Every row below is a RECORD that a notification was due — nothing was
 * ever sent, and status can only ever read NOT_SENT."* That was true when it was
 * written and false four tasks later: `054_notification_delivery.sql` built the
 * outbox, `recordNotificationEvent` began writing `'QUEUED'`,
 * `runNotificationOutboxOnce` began handing rows to a transport, and
 * `SmtpMailTransport` became a transport that genuinely reaches a relay. A
 * hardcoded honesty notice is a claim nothing keeps true — the identical shape
 * S-016 records for a safety claim keyed on a deployment name — so the notice is
 * now DERIVED from this value, and this value is derived from the transport the
 * composition root actually constructed.
 *
 * - `none` — no mail transport is wired into this deployment. Every delivery row
 *   stays `QUEUED`, is never attempted (no attempt-ledger row is written, and no
 *   retry budget is consumed — `notification-outbox.ts`), and nothing reaches
 *   anybody. This is the honest state of a deployment with no relay endpoint or
 *   credential, and it is a DEPLOYMENT gap rather than a code gap.
 * - `simulated` — a transport declaring `simulates: true` is wired
 *   (`SimulatedMailTransport`). Delivery rows reach the terminal status
 *   `SIMULATED`, carrying a receipt no relay ever issued. No mail leaves the
 *   building. This is what a demonstration deployment wires.
 * - `live` — a transport that genuinely reaches a relay is wired
 *   (`SmtpMailTransport`). A delivery row reaching `SENT` means a real mail
 *   server accepted a real message for a real recipient.
 *
 * ## What this deliberately does NOT claim
 *
 * ⚠ **It is a fact about the TRANSPORT, never about any one message.** `live`
 * does not mean a given notification was delivered — the outbox row's own status
 * says that, one row at a time, and `054_notification_delivery.sql` made
 * `SIMULATED` a distinct terminal status precisely so a reader never has to know
 * which transport was configured to tell whether mail actually went out. This
 * field answers "could anything sent from here reach a real mailbox", which is
 * the question a NOTICE on a list response has to answer before the list is read.
 *
 * ⚠ **`simulated` is not a weaker `live`.** A simulated transport contacts
 * nothing at all: it is the only value besides `none` that Demonstration Mode
 * permits (`assertDemonstrationModeSafe`), because a demonstration that could
 * email a real officer is not a demonstration.
 */
export type NotificationDelivery = 'none' | 'simulated' | 'live'

/** Every value {@link NotificationDelivery} admits, for narrowing at a boundary. */
export const NOTIFICATION_DELIVERIES: readonly NotificationDelivery[] = ['none', 'simulated', 'live']

/**
 * Narrow an unknown value — a parsed JSON body, typically — to a
 * {@link NotificationDelivery}.
 *
 * Returns `false` for anything unrecognised rather than defaulting to one, the
 * same discipline {@link isSettlementDispatch} follows and for the same reason:
 * `none` is the dangerous default here too, because it is the value that
 * licenses "nothing was ever sent".
 */
export function isNotificationDelivery(value: unknown): value is NotificationDelivery {
  return typeof value === 'string' && (NOTIFICATION_DELIVERIES as readonly string[]).includes(value)
}

/**
 * What ONE response actually said about `notificationDelivery`.
 *
 * ⚠ Identical in shape to `SettlementDispatchReport`/`DemonstrationModeReport`,
 * for the reason S-017 records rather than for symmetry: a field the server did
 * not send arrives as `undefined` however confidently a client's `interface`
 * declares it a `string`. An older gateway behind a newer bundle must not be
 * able to produce the reassuring branch by saying nothing — `reported: null` is
 * "it answered and did not say", which is not evidence that nothing can be sent.
 */
export interface NotificationDeliveryReport {
  readonly reported: string | null
}

/**
 * Read a response's `notificationDelivery` into a
 * {@link NotificationDeliveryReport}.
 *
 * ⚠ No trimming, no case folding, no normalisation — `'  '` is a value the
 * server sent, matches nothing, and must surface as unrecognised rather than be
 * tidied into silence. Only a genuinely empty string is "said nothing".
 */
export function readNotificationDelivery(value: unknown): NotificationDeliveryReport {
  return { reported: typeof value === 'string' && value.length > 0 ? value : null }
}
